import {useState, useEffect, useCallback, useRef} from 'react';
import type {ProTipContext, AiPromptBoxProTipProps} from '../components/ai-prompt-box-pro-tip/ai-prompt-box-pro-tip.types';
import {ProTipVariant} from '../components/ai-prompt-box-pro-tip/ai-prompt-box-pro-tip.types';
import type {AiChatProTipType} from '../components/ai-prompt-box-pro-tip/ai-chat-pro-tip-type';
import {
  getSeenTipsFromLocalStorage,
  saveSeenTipsToLocalStorage,
  fetchSeenTipsFromBackend,
  markTipSeenOnBackend,
  getStoredResetVersion,
  setStoredResetVersion,
} from './pro-tips/pro-tip-persistence';
import {findMatchingProTip} from './pro-tips/pro-tip-triggers';
import type {ProTipConfig} from '../components/ai-prompt-box-pro-tip/ai-chat-pro-tip-config';
import {PRO_TIP_COOLDOWN_MS, isMarkSeenOnDismissTip, isPersistentTip} from '../components/ai-prompt-box-pro-tip/ai-chat-pro-tip-config';
import type {VoidFunction} from '@Utils/general.util';

interface UseProTipsParams {
  enabled: boolean;
  prompt: string;
  attachmentCount: number;
  context?: ProTipContext;
  isMobile: boolean;
}

type DistributiveOmit<T, K extends keyof AiPromptBoxProTipProps> = T extends unknown ? Omit<T, K> : never;
type ActiveProTipState = DistributiveOmit<AiPromptBoxProTipProps, 'onDismiss'>;

interface UseProTipsResult {
  activeProTip: ActiveProTipState | null;
  dismissActiveProTip: VoidFunction;
}

function selectRandomMessage(messages: string[]): string {
  const index = Math.floor(Math.random() * messages.length);
  return window.i18next.t(messages[index]);
}

function pickResponsiveKey(desktop: string, mobile: string | undefined, isMobile: boolean): string {
  return isMobile && mobile ? mobile : desktop;
}

function buildActiveProTipState(config: ProTipConfig, isMobile: boolean): ActiveProTipState {
  const {display} = config;
  if (display.variant === ProTipVariant.TEXT) {
    return {
      type: config.type,
      variant: ProTipVariant.TEXT,
      icon: display.icon,
      message: selectRandomMessage(display.messages),
    };
  }
  const title = window.i18next.t(pickResponsiveKey(display.titleKey, display.mobileTitleKey, isMobile));
  const description = window.i18next.t(pickResponsiveKey(display.descriptionKey, display.mobileDescriptionKey, isMobile));
  if (display.variant === ProTipVariant.IMAGE) {
    return {type: config.type, variant: ProTipVariant.IMAGE, imageUrl: display.imageUrl, title, description};
  }
  return {type: config.type, variant: ProTipVariant.COMPONENT, VisualComponent: display.VisualComponent, slotLayout: display.slotLayout, title, description};
}

export function useProTips({enabled, prompt, attachmentCount, context, isMobile}: UseProTipsParams): UseProTipsResult {
  const [seenTips, setSeenTips] = useState<Set<string>>(() => getSeenTipsFromLocalStorage());
  const [activeProTip, setActiveProTip] = useState<ActiveProTipState | null>(null);
  const [shownInChat, setShownInChat] = useState<Set<string>>(new Set());
  const backendFetched = useRef(false);
  const prevAttachmentCount = useRef(attachmentCount);
  const prevChatId = useRef(context?.chatId);
  const lastDismissedAt = useRef<number>(0);

  useEffect(() => {
    if (context?.chatId !== prevChatId.current) {
      setShownInChat(new Set());
      setActiveProTip(null);
      prevChatId.current = context?.chatId;
    }
  }, [context?.chatId]);

  useEffect(() => {
    if (!enabled || backendFetched.current) {
      return;
    }
    backendFetched.current = true;

    void fetchSeenTipsFromBackend().then((response) => {
      if (response.resetVersion === null || response.resetVersion === getStoredResetVersion()) {
        return;
      }
      const fresh = new Set(response.tips);
      saveSeenTipsToLocalStorage(fresh);
      setStoredResetVersion(response.resetVersion);
      setSeenTips(fresh);
    });
  }, [enabled]);

  const rememberInChat = useCallback((tipType: AiChatProTipType): void => {
    setShownInChat((prev) => {
      if (prev.has(tipType)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(tipType);
      return next;
    });
  }, []);

  const persistSeen = useCallback((tipType: AiChatProTipType): void => {
    if (isPersistentTip(tipType)) {
      return;
    }

    setSeenTips((prev) => {
      if (prev.has(tipType)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(tipType);
      saveSeenTipsToLocalStorage(next);
      return next;
    });
    markTipSeenOnBackend(tipType);
  }, []);

  const markShown = useCallback((tipType: AiChatProTipType): void => {
    rememberInChat(tipType);

    if (isMarkSeenOnDismissTip(tipType)) {
      return;
    }

    persistSeen(tipType);
  }, [rememberInChat, persistSeen]);

  const dismissActiveProTip = useCallback((): void => {
    if (activeProTip) {
      rememberInChat(activeProTip.type);
      persistSeen(activeProTip.type);
    }
    setActiveProTip(null);
    lastDismissedAt.current = Date.now();
  }, [activeProTip, rememberInChat, persistSeen]);

  useEffect(() => {
    if (!enabled || activeProTip || !context?.chatId) {
      return;
    }

    if (lastDismissedAt.current > 0 && Date.now() - lastDismissedAt.current < PRO_TIP_COOLDOWN_MS) {
      return;
    }

    const winner = findMatchingProTip(
      {prompt, attachmentCount, prevAttachmentCount: prevAttachmentCount.current, context},
      seenTips,
      shownInChat,
    );

    prevAttachmentCount.current = attachmentCount;

    if (!winner) {
      return;
    }

    setActiveProTip(buildActiveProTipState(winner, isMobile));

    markShown(winner.type);
  }, [enabled, prompt, attachmentCount, context, seenTips, shownInChat, activeProTip, markShown, isMobile]);

  return {
    activeProTip: enabled ? activeProTip : null,
    dismissActiveProTip,
  };
}
